import { NumberRange } from "@tento-core/range";
import { ScrollWindow } from "./scroll-window";
import { RenderedStrip } from "@tento-core/sheet-layout/rendered-strip";

// The DOM scroll position on the large virtual strip can differ from a
// fractional column boundary by up to one CSS pixel.
const SCROLL_POSITION_TOLERANCE_PX = 1;

export class ScrollViewport {
  constructor(
    readonly props: ScrollViewport.Props,
    private readonly window: ScrollWindow,
    private readonly contentOffset: () => number,
  ) {}

  readonly scrollOffset = ((self) => ({
    start(index: number): Option<number> {
      const off = self.window.getOffset(index);
      if (off == null) return null;
      return off - self.props.startOccludedPx;
    },
    center(index: number, width: number): Option<number> {
      const start = this.start(index);
      if (start == null) return null;
      return Math.floor(start - (self.visible.length - width) / 2);
    },
    end(index: number, width: number): Option<number> {
      const start = this.start(index);
      if (start == null) return null;
      return Math.floor(start - self.visible.length + width);
    },
    position(
      index: number,
      width: number,
      position: "start" | "center" | "end",
    ): Option<number> {
      switch (position) {
        case "start":
          return this.start(index);
        case "center":
          return this.center(index, width);
        case "end":
          return this.end(index, width);
      }
    },
    nearest(cutoff: number = 0.5): Option<number> {
      const vir = self.visible.indexRange;
      if (vir.start == null) return null;
      const prevOffset = self.window.getOffset(vir.start);
      if (prevOffset == null) return null;
      const nextOffset = self.window.getOffset(vir.start + 1);
      if (nextOffset == null) return null;
      const width = nextOffset - prevOffset;
      if (width <= 0) return null;
      const progress = (self.visible.offsetStart - prevOffset) / width;

      if (
        Math.min(
          Math.abs(self.visible.offsetStart - prevOffset),
          Math.abs(nextOffset - self.visible.offsetStart),
        ) <= SCROLL_POSITION_TOLERANCE_PX
      ) return null;
      if (progress <= 0.005) return null;
      if (progress >= 0.995) return vir.start + 1;
      if (progress < cutoff) {
        return vir.start;
      } else {
        return vir.start + 1;
      }
    },
  }))(this);

  readonly visible = ((self) => ({
    get length(): number {
      return (
        self.props.length -
        self.props.startOccludedPx -
        self.props.endOccludedPx
      );
    },
    get offsetStart(): number {
      return self.contentOffset() + self.props.startOccludedPx;
    },
    get offsetRange(): NumberRange {
      const vps = this.offsetStart;
      return new NumberRange(vps, vps + this.length);
    },
    get offsetCenter(): number {
      const vis = this.offsetRange;
      return (vis.start + vis.end) / 2;
    },
    /**
     * The columns under the visible strip. Both bounds are real indices of the
     * live window even when the strip runs off the end of it — the window
     * answers with the span it reached rather than with `null`s typed `number`
     * (see {@link ScrollWindow.findIndiciesFromOffsetRange}), which is what
     * lets `.start` be read as an anchor without a guard at every call site.
     */
    get indexRange(): NumberRange {
      return self.window.findIndiciesFromOffsetRange(this.offsetRange);
    },
    /** The left-edge content anchor, allowing for DOM pixel rounding. Coverage
     * keeps the containing column; translating its subpixel sliver across a
     * day/week boundary would instead move the view by an entire week. */
    get anchorIndex(): number {
      const index = this.indexRange.start;
      const previous = self.window.getOffset(index);
      const next = self.window.getOffset(index + 1);
      if (previous == null || next == null) return index;
      const remaining = next - this.offsetStart;
      return remaining >= 0 && remaining <= SCROLL_POSITION_TOLERANCE_PX &&
          remaining < this.offsetStart - previous
        ? index + 1
        : index;
    },
    _indexStartOptimistic: null as Option<number>,
    get indexStartOptimistic(): number {
      return this._indexStartOptimistic ?? this.indexRange.start;
    },
    get state(): {
      offset: NumberRange;
      index: NumberRange;
    } {
      const or = this.offsetRange;
      return {
        offset: or,
        index: self.window.findIndiciesFromOffsetRange(or),
      };
    },
  }))(this);

  readonly rendered = ((self) => ({
    /** The visible strip plus its culled flanks (`RenderedStrip.VIEWPORT`, the rule the headless CLI binds by too). */
    get offsetRange(): NumberRange {
      const strip = RenderedStrip.offsetRange(self.visible.offsetStart, self.visible.length, RenderedStrip.VIEWPORT);
      return new NumberRange(strip.start, strip.end);
    },
    /** @see {@link ScrollViewport.visible.indexRange} — same guarantee: an
     *  index range the window can name, never a null-bearing one. */
    get indexRange(): NumberRange {
      return self.window.findIndiciesFromOffsetRange(this.offsetRange);
    },
    get state(): {
      offset: NumberRange;
      index: NumberRange;
    } {
      const or = this.offsetRange;
      return {
        offset: or,
        index: self.window.findIndiciesFromOffsetRange(or),
      };
    },
  }))(this);
}

export namespace ScrollViewport {
  export type Props = {
    startOccludedPx: number;
    endOccludedPx: number;
    length: number;
    minIndex?: Option<number>;
    maxIndex?: Option<number>;
  };

  export namespace Props {
    export function equals(t: Props, o: Option<Props>): boolean {
      if (!o) return false;
      return (
        t.startOccludedPx === o.startOccludedPx &&
        t.endOccludedPx === o.endOccludedPx &&
        t.length === o.length
      );
    }
  }
}
